/**
 * Personal News 피드백 설문 — Mini App 표현층
 *
 * Slack 은 모달로 받는다. 텔레그램에는 모달이 없으므로 리포트 하단에 인라인으로 둔다.
 * 항목/라벨은 서버가 내려준다(pnr_feedback.SURVEY) — 클라이언트와 어긋날 일이 없다.
 *
 * 저장만 하면 되고, 파라미터 조정은 agent-manager 가 processed=FALSE 인 행을 읽어 처리한다.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { haptic } from '../../hooks/useTelegram';

const API_URL = '/api';

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

export const FeedbackSurvey = ({ reportId }) => {
    const [survey, setSurvey] = useState([]);
    const [answers, setAnswers] = useState({});
    const [saving, setSaving] = useState(null);
    const [available, setAvailable] = useState(true);
    const [confirmReset, setConfirmReset] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [resetDone, setResetDone] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/pnr/feedback/survey`, {
                    headers: authHeaders(),
                });
                setSurvey(data.survey || []);
                setAnswers(data.answers || {});
            } catch (e) {
                // 로그인하지 않았거나(401) 서버가 아직 이 API 를 모르면 설문을 감춘다.
                setAvailable(false);
            }
        };
        load();
    }, []);

    const choose = async (dimension, feedbackType) => {
        haptic('light');
        const prev = answers[dimension];
        setAnswers((a) => ({ ...a, [dimension]: feedbackType })); // 낙관적 반영
        setSaving(dimension);
        try {
            await axios.post(
                `${API_URL}/pnr/feedback`,
                { dimension, feedback_type: feedbackType, report_id: reportId },
                { headers: authHeaders() }
            );
        } catch (e) {
            setAnswers((a) => ({ ...a, [dimension]: prev })); // 실패하면 되돌린다
        } finally {
            setSaving(null);
        }
    };

    const resetProfile = async () => {
        haptic('medium');
        setResetting(true);
        try {
            await axios.post(`${API_URL}/pnr/profile/reset`, {}, { headers: authHeaders() });
            setAnswers({});
            setConfirmReset(false);
            setResetDone(true);
        } catch (e) {
            /* 실패하면 확인 상태를 유지해 다시 시도할 수 있게 둔다 */
        } finally {
            setResetting(false);
        }
    };

    if (!available || survey.length === 0) return null;

    return (
        <section className="tg-survey">
            <h3 className="tg-survey-title">이번 추천은 어떠셨나요?</h3>
            <p className="tg-hint tg-survey-desc">
                답해주시면 다음 추천에 반영됩니다. 언제든 다시 고르실 수 있습니다.
            </p>

            {survey.map((item) => (
                <div key={item.dimension} className="tg-survey-item">
                    <p className="tg-survey-q">{item.question}</p>
                    <div className="tg-choices">
                        {item.options.map((opt) => {
                            const selected = answers[item.dimension] === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`tg-choice${selected ? ' is-selected' : ''}`}
                                    disabled={saving === item.dimension}
                                    onClick={() => choose(item.dimension, opt.value)}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* 프로필 초기화 — 잘못 학습되었을 때 빠져나오는 유일한 수단이다.
                되돌릴 수 없으므로 두 단계로 받는다. */}
            <div className="tg-reset">
                {resetDone ? (
                    <p className="tg-hint">
                        초기화했습니다. 다음 리포트부터 처음부터 다시 학습합니다.
                    </p>
                ) : confirmReset ? (
                    <>
                        <p className="tg-hint tg-reset-warn">
                            학습된 취향과 지금까지의 피드백이 모두 지워집니다. 되돌릴 수 없습니다.
                        </p>
                        <div className="tg-choices">
                            <button
                                type="button"
                                className="tg-choice"
                                onClick={() => setConfirmReset(false)}
                                disabled={resetting}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                className="tg-choice tg-danger"
                                onClick={resetProfile}
                                disabled={resetting}
                            >
                                {resetting ? '지우는 중…' : '초기화'}
                            </button>
                        </div>
                    </>
                ) : (
                    <button
                        type="button"
                        className="tg-linkish"
                        onClick={() => {
                            haptic('light');
                            setConfirmReset(true);
                        }}
                    >
                        추천이 계속 안 맞나요? 취향 초기화
                    </button>
                )}
            </div>
        </section>
    );
};

export default FeedbackSurvey;
